import { Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { AppConfigService, OpCo } from '@eucoms/common-components';
import { mockConfigValues } from '@app/mocks/services/config';
import { of, Subject, Subscription } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { environment } from '@environments/environment';
import { MsalService } from '@azure/msal-angular';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  opCoForBranding: OpCo = this.appConfig.config.opCo;
  linkLogo: HTMLAnchorElement;
  imgLogo: HTMLImageElement;
  imgLogoSm: HTMLImageElement;
  footerMessage: HTMLElement;
  opCoSelected: string = '';
  tabs = [
    {
      label: 'Summaries',
    },
    {
      label: 'Touchpoints',
    },
    {
      label: 'Real-Time Preview',
    },
    {
      label: 'Log',
    },
    {
      label: 'Configuration',
    }
  ];
  tabUrls = [
    "summary",
    "touchpoints",
    "preview",
    "log",
    "config"
  ];
  selectedTabIndex: number = 0;
  routeSubscription: Subscription | undefined;
  showMainContent: boolean = false;
  isUserAuthenticated = false;

  constructor(
    private appConfig: AppConfigService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: MsalService,
  ) {}

  get fullName(): string {
    return "Sign Out - " + this.authService.instance.getActiveAccount().name;
  }

  ngOnInit(): void {

    this.routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.init();
      }
    });
    this.isUserAuthenticated = this.authService.instance.getAllAccounts().length > 0;
    this.init();
  }

  private init() {
    const initialNewOpCo = this.route.firstChild?.snapshot.params['opco'];
    if (initialNewOpCo) {
      console.log('Initial route opco parameter (snapshot):', initialNewOpCo);
      this.opCoValueChanged(initialNewOpCo); // Process initial value
      this.updateActiveTabBasedOnUrl(this.router.url); // Update active tab based on URL
    }
  }

  opCoValueSelected(newOpCo: string | undefined): void {
    console.log('OpCo value selected:', newOpCo);
    location.href = `/${newOpCo.toLowerCase()}`; // Redirect to the selected OpCo
  }

  // Centralized handler for OpCo parameter changes
  opCoValueChanged(newOpCo: string | undefined): void {
    // Only update if the OpCo has actually changed
    if (this.opCoSelected !== newOpCo) {
      const validOpcos = Object.keys(OpCo);
      this.opCoSelected = validOpcos.find(opCo => opCo.toLowerCase() === newOpCo?.toLowerCase()) ?? 'select-opco';

        if (this.opCoSelected === 'select-opco') {
            this.showMainContent = false; // No OpCo selected
        } else {
            this.configureAppForOpCo(this.opCoSelected);
            this.showMainContent = true;
        }
    } else {
        // OpCo hasn't changed, but ensure showMainContent reflects current state
        this.showMainContent = !!this.opCoSelected;
    }
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe(); // Prevent memory leaks
    }
  }

  ngAfterViewInit(): void {
    this.changeHeaderandFootertoExelon();
  }

  // Helper to update active tab based on the current URL segment
  private updateActiveTabBasedOnUrl(url: string): void {
    // Remove query parameters and hash from URL for tab matching
    const urlWithoutParams = url.split('?')[0].split('#')[0];
    const urlSegments = urlWithoutParams.split('/');
    const opcoIndex = urlSegments.findIndex(segment => segment.toLowerCase() === this.opCoSelected.toLowerCase());
    const currentTabPath = (opcoIndex !== -1 && urlSegments.length > opcoIndex + 1)
                            ? urlSegments[opcoIndex + 1]
                            : 'summary';

    const activeIndex = this.tabUrls.findIndex(tabUrl => tabUrl === currentTabPath);
    const newTabIndex = activeIndex !== -1 ? activeIndex : 0;

    if (this.selectedTabIndex !== newTabIndex) {
        console.log(`URL: ${url}, CurrentTabPath: ${currentTabPath}, ActiveIndex updated to: ${newTabIndex}`);
        this.selectedTabIndex = newTabIndex;
    }
  }

  // Update the header logo and footer message to Exelon
  changeHeaderandFootertoExelon(): void {
    this.linkLogo = (document.querySelector('app-euds-smart-header-simple').shadowRoot.querySelector('app-euds-header-simple').shadowRoot.querySelector('a') as HTMLAnchorElement);
    this.linkLogo.href = '/';

    this.imgLogo = (document.querySelector('app-euds-smart-header-simple').shadowRoot.querySelector('app-euds-header-simple').shadowRoot.querySelector('img.logo') as HTMLImageElement);
    this.imgLogo.src = 'assets/exelon-header-logo-white.svg';
    this.imgLogo.alt = 'Exelon Corporation';

    this.imgLogoSm = (document.querySelector('app-euds-smart-header-simple').shadowRoot.querySelector('app-euds-header-simple').shadowRoot.querySelector('img.logo-sm') as HTMLImageElement);
    this.imgLogoSm.src = 'assets/exelon-header-logo-white.svg';
    this.imgLogoSm.alt = 'Exelon Corporation';

    this.footerMessage = (document.querySelector('app-euds-smart-footer-simple').shadowRoot.querySelector('app-euds-footer-simple').shadowRoot.querySelector('p.message') as HTMLElement);
    this.footerMessage.innerHTML = 'Copyright © Exelon Corporation. All Rights Reserved.';
  }

  // Update Logo URL
  updateLogoURL(opcoValue: string): void {
    this.linkLogo = (document.querySelector('app-euds-smart-header-simple').shadowRoot.querySelector('app-euds-header-simple').shadowRoot.querySelector('a') as HTMLAnchorElement);
    if (!opcoValue) {
      this.linkLogo.href = '/';
    }
    else {
      this.linkLogo.href = '/' + encodeURIComponent(opcoValue.toLowerCase());
    }
  }

  tabChangeFunction(event: any): void {
    this.selectedTabIndex = event.index;
    console.log('Show ' + this.tabs[this.selectedTabIndex].label) + ' page.';
    this.navigateToTab(this.selectedTabIndex);
  }

  // Navigate to the selected tab
  navigateToTab(tabIndex: number): void {
    this.selectedTabIndex = tabIndex;
    const tabUrl = this.tabUrls[this.selectedTabIndex];
    console.log('Navigating to tab:', tabUrl);
    this.router.navigate([this.opCoSelected.toLowerCase(), tabUrl]);
  }

  // Log the user out
  logout() {
    this.authService.logoutRedirect();
  }


  // Configure AppConfig and Branding based on the selected OpCo
  configureAppForOpCo(opcoValue: string): void {
    this.updateLogoURL(opcoValue);

    if (!opcoValue) {
        this.opCoForBranding = null;
        return;
    }
    this.opCoForBranding = OpCo[opcoValue as keyof typeof OpCo] ?? null;

    if (this.appConfig) {
        const opCoEnum = OpCo[opcoValue as keyof typeof OpCo];
        const opCoConfig = mockConfigValues[opcoValue as keyof typeof mockConfigValues];
        if (opCoEnum && opCoConfig) {
            this.appConfig.config$ = of({ opCo: opCoEnum }).pipe(delay(1));
            this.appConfig.config = {
                opCo: opCoEnum,
                anonBaseUrl: opCoConfig.anonBaseUrl,
                baseUrl: opCoConfig.baseUrl,
            };
            console.log(`AppConfig updated for ${opcoValue}`);
        } else {
             console.warn(`No mock config found for OpCo: ${opcoValue}`);
        }
        this.appConfig.config = {
          ... this.appConfig.config ?? {},
          ... environment.appConfig.common ?? {},
          ... environment.appConfig[opcoValue as keyof typeof environment.appConfig] ?? {},
        };
        console.log('AppConfig updated:', this.appConfig.config);
    }
  }
}
